// Refactored App.js with improved authentication flow, Firebase action routes, and Toast notifications

import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import UserProfile from './components/UserProfile';
import NotFound from './components/NotFound';
import Footer from './components/Footer';
import CookieNotice from './components/CookieNotice';
import PaymentSuccess from './components/PaymentSuccess';
import VerificationSuccessPage from './components/VerificationSuccessPage';
import EmailVerified from './components/EmailVerified';
import { AuthProvider, useAuth, ProtectedRoute } from './context/AuthProvider';
import { ToastProvider } from './context/ToastContext'; // Import Toast Provider

// Import the new Firebase action handlers
import ResetPasswordHandler from './components/auth/ResetPasswordHandler';
import VerifyEmailHandler from './components/auth/VerifyEmailHandler';
import AuthActionHandler from './components/auth/AuthActionHandler';

// This inner component uses our AuthContext to decide what to show
function AppContent() {
  const location = useLocation();
  const { currentUser, loading } = useAuth();

  // Show nothing while checking auth (you could show a spinner if you want)
  if (loading) {
    return null;
  }

  // Figure out if we're on a user profile route
  // by checking for a single path segment that's not known
  const segments = location.pathname.split('/').filter(Boolean);
  const isUserProfile =
    segments.length === 1 && 
    !['dashboard', 'payment-success', 'email-verified', 'reset-password', 'verify-email', '__'].includes(segments[0]);

  return (
    <>
      <CookieNotice />
      <div className="App">
        <Routes>
          {/* Firebase Auth Action Handler - This must come before other routes */}
          <Route path="/custom-auth-action" element={<AuthActionHandler />} />

          {/* Route for / - redirect logged-in users to dashboard */}
          <Route
            path="/"
            element={
              currentUser ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Header />
              )
            }
          />

          {/* Protected route for Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route path="/payment-success" element={<PaymentSuccess />} />
          
          {/* Email verification route */}
          <Route path="/email-verified" element={<EmailVerified />} />

          {/* Verification success page */}
          <Route path="/verification-success" element={<VerificationSuccessPage />} />

          {/* Firebase Action URLs - New Routes */}
          <Route path="/reset-password" element={<ResetPasswordHandler />} />
          <Route path="/verify-email" element={<VerifyEmailHandler />} />

          {/* Catch-all for any single-segment path => user profiles */}
          <Route path="/:username" element={<UserProfile />} />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Show footer unless we're on a user profile */}
        {!isUserProfile && <Footer />}
      </div>
    </>
  );
}

// Wrap AppContent with BrowserRouter + AuthProvider + ToastProvider
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;